import { useState, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import {
  Box,
  CircularProgress,
  Divider,
  Grid,
} from '@mui/material';

import { ContainerMain, ContainerButtons, styles } from './styles';
import { Text, CollaboratorAdhesionGraph, EmptyList } from '../../components';
import { BirthdayCollaborator, HomeButton } from './components';
import ModalAddCollaborator from '../Collaborators/components/ModalAddCollaborator';
import MessageService from '../../services/napoleon/MessageService';
import RequestRHService from '../../services/napoleon/RequestRHService';
import ComplaintsService from '../../services/napoleon/ComplaintsService';
import CollaboratorService from '../../services/napoleon/CollaboratorService';
import { percentage } from '../../utils';

import {
  boardIcon,
  cardIcon,
  employeesIcon,
  messageIcon,
} from '../../assets/images/iconsQuickAction';
import { emptyMessageIcon, emptyUsersIcon } from '../../assets/images/icons';

export default function Home() {
  const history = useHistory();
  const complaintsDispatch = useDispatch();
  const requestsDispatch = useDispatch();
  const { newComplaints } = useSelector((state) => state.complaintsReducer);
  const { newRequests } = useSelector((state) => state.requestsReducer);
  const messageService = new MessageService();
  const requestRHService = new RequestRHService();
  const complaintsService = new ComplaintsService();
  const collaboratorService = new CollaboratorService();
  const containerBirthdaysRef = useRef(null);
  const [birthdays, setBirthdays] = useState([]);
  const [messages, setMessages] = useState([]);
  const [pageBirthday, setPageBirthday] = useState(1);
  const [loadingBirthdays, setLoadingBirthdays] = useState(false);
  const [hasMoreBirthdays, setHasMoreBirthdays] = useState(true);
  const [showModalAddCollaborator, setShowModalAddCollaborator] = useState(false);
  const homeButtons = [
    {
      imagePath: boardIcon,
      text: 'Nova postagem no mural',
      route: '/admin/create-message',
      params: { path: 'home' },
    },
    {
      imagePath: messageIcon,
      text: 'Enviar nova mensagem direta',
      route: '/admin/direct-message',
    },
    {
      imagePath: cardIcon,
      text: 'Recarregar benefícios nos cartões',
      isExternalRoute: true,
      route: 'https://empresa.volus.com',
    },
    {
      imagePath: employeesIcon,
      text: 'Adicionar colaboradores',
      onClick: () => setShowModalAddCollaborator(true),
    },
  ];

  const MessagesSection = () => {
    if (messages.length > 0) {
      return messages.map((message, key) => (
        <Grid item container xs={12} style={styles.gridItem} key={key}>
          <Text variant="hintText">{message.subject}</Text>
          <Text variant="hintText">{percentage(message.readRate)}%</Text>
        </Grid>
      ));
    }

    return <EmptyList srcImage={emptyMessageIcon} text="Não há nenhuma mensagem no mural." />;
  };

  function handleNavigation(prop) {
    if (prop.isExternalRoute) {
      return window.open(prop.route, '_blank');
    }

    if (prop.route) {
      return history.push(prop.route, prop.params);
    }

    if (prop.onClick) {
      return prop.onClick();
    }

    return null;
  }

  const getMessages = () => {
    messageService.getMessagesList(1, 4).then((response) => {
      const { docs } = response.data;
      setMessages(docs);
    });
  };

  const getNewRequests = () => {
    requestRHService
      .getNewRequests()
      .then((response) => {
        requestsDispatch({ type: 'FIND_REQUESTS', newRequests: response.data.amount });
      })
      .catch(() => {
        requestsDispatch({ type: 'FIND_REQUESTS', newRequests: 0 });
      });
  };

  const getAmountNewComplaints = () => {
    complaintsService
      .getAmountNewComplaints()
      .then((response) => {
        complaintsDispatch({ type: 'FIND_COMPLAINTS', newComplaints: response.data.amount });
      })
      .catch(() => {
        complaintsDispatch({ type: 'FIND_COMPLAINTS', newComplaints: 0 });
      });
  };

  async function getBirthdays(paginate = 10) {
    return collaboratorService.getBirthdays(pageBirthday, paginate)
      .then((res) => {
        const newBirthdays = res.data?.docs || [];

        if (newBirthdays.length === 0) {
          setHasMoreBirthdays(false);
          return;
        }

        setBirthdays((prev) => [...prev, ...newBirthdays]);
        setHasMoreBirthdays(newBirthdays.length === paginate);
      })
      .catch(() => {
        setBirthdays([]);
        setHasMoreBirthdays(false);
      });
  }

  function handleGetBirthdays() {
    if (loadingBirthdays || !hasMoreBirthdays) return;

    setLoadingBirthdays(true);
    setPageBirthday((prev) => prev + 1);
    getBirthdays().finally(() => setLoadingBirthdays(false));
  }

  const handleScroll = () => {
    const { scrollLeft, scrollWidth, clientWidth } = containerBirthdaysRef.current;
    if (scrollLeft + clientWidth >= scrollWidth - 10) {
      handleGetBirthdays();
    }
  };

  // NOTE: Do not refactor 'birthdaysSection()' into a React component (e.g., <BirthdaysSection />).
  // Keeping it as a function ensures the DOM is not unnecessarily recreated on re-renders,
  // which prevents the horizontal scroll position from resetting when new items are added.
  // This approach is intentional to maintain a smooth user experience.
  const birthdaysSection = () => {
    // Initial screen state
    if (hasMoreBirthdays && birthdays.length === 0) {
      return (
        <Box sx={styles.loadingCardBirthday} width={'100%'}>
          <CircularProgress color='primary' size={25} /> <br />
          <Text variant='hintText'>Carregando aniversariantes...</Text>
        </Box>
      );
    }

    if (birthdays.length > 0) {
      return (
        <Box ref={containerBirthdaysRef} onScroll={handleScroll} sx={styles.cardContainer}>
          {birthdays.map((collaborator, index) => (
            <BirthdayCollaborator collaborator={collaborator} key={index} />
          ))}

          {hasMoreBirthdays && loadingBirthdays && (
            <Box sx={styles.loadingCardBirthday} minWidth={200}>
              <CircularProgress color='primary' size={25} />
            </Box>
          )}
        </Box>
      );
    }

    return (
      <Box marginBottom={6}>
        <EmptyList srcImage={emptyUsersIcon} text="Nenhum aniversariante encontrado." />
      </Box>
    );
  };

  useEffect(() => {
    handleGetBirthdays();
    getMessages();
    getNewRequests();
    getAmountNewComplaints();
  }, []);

  return (
    <ContainerMain>
      <Text variant="h3">Ações rápidas</Text>

      <ContainerButtons>
        <Grid container rowSpacing={1}>
          {homeButtons.map((prop, key) => (
            <Grid item xs={6} lg={3} container key={key}>
              <HomeButton
                imagePath={prop.imagePath}
                text={prop.text}
                onClick={() => handleNavigation(prop)}
                comingSoon={prop.comingSoon}
              />
            </Grid>
          ))}
        </Grid>
      </ContainerButtons>

      <Divider sx={styles.spacing} />

      <Text variant="h3">Aniversários</Text>
      {birthdaysSection()}

      <Grid container columnSpacing={2} rowSpacing={1} style={styles.containerGridRow}>
        {/* Left side */}
        <Grid item lg={6} md={6} sm={12} xs={12} style={styles.containerGridRow}>
          <Grid container style={styles.containerGridRow}>
            <Text variant="h3">Adesão ao app</Text>
            <CollaboratorAdhesionGraph calledByHome={true} />
          </Grid>
        </Grid>

        {/* Right side */}
        <Grid item lg={6} md={6} sm={12} xs={12} style={styles.containerGridReadRate}>
          <Grid container style={styles.containerGridRow}>
            <Text variant="h3">Visualizações das últimas postagens</Text>
            <MessagesSection />
          </Grid>
        </Grid>
      </Grid>

      <Divider sx={styles.spacing} />

      <Grid container spacing={1} rowSpacing={2} style={styles.containerGridRow}>
        <Grid item lg={6} md={6} sm={6} xs={12} container style={styles.gridItemRequestsComplaints}>
          <Text variant="h3" style={styles.spacingBottom}>
            Solicitações recentes
          </Text>
          <HomeButton
            title={newRequests.toString()}
            text="Novas Solicitações"
            onClick={() => handleNavigation({ route: '/admin/requests' })}
          />
        </Grid>

        <Grid item lg={6} md={6} sm={6} xs={12} container style={styles.gridItemRequestsComplaints}>
          <Text variant="h3" style={styles.spacingBottom}>
            Denúncias
          </Text>
          <HomeButton
            title={newComplaints.toString()}
            text="Novas Denúncias"
            onClick={() => handleNavigation({ route: '/admin/complaints' })}
          />
        </Grid>
      </Grid>

      <ModalAddCollaborator
        openModal={showModalAddCollaborator}
        setOpenModal={setShowModalAddCollaborator}
      />
    </ContainerMain>
  );
}
